/**
 * سربرگِ «تستِ دستی» — مایگریشن‌های 0161–0166، صفحه‌ی تستر در qa/ (qa.palsandplans.com).
 *
 * به ترتیبی که صاحبِ پروژه کار می‌کند:
 *   ۱. تسترها — چه کسی وارد صفحه‌ی تست می‌شود، کدام پیش‌فرض است، و ایمیل‌هایی
 *      که دعوت شده‌اند ولی هنوز حساب نساخته‌اند.
 *   ۲. پیشرفت، باگ‌ها و نتیجه‌ها — همان چیزی که قبلاً با فایلِ JSON دستی می‌رسید.
 *   ۳. تیکت‌ها — هر اساین یک «تیکت» است: تیکتِ Jira یا یک اسمِ آزاد (0166)؛ یک
 *      سناریو در چند تیکت به کار می‌رود و نتیجه‌اش در هر تیکت جداست.
 *   ۴. جدولِ سناریوها — فیلترِ «و/یا»، بعد گذاشتنِ ردیف‌های انتخاب‌شده در یک
 *      تیکت و اساینِ آن تیکت به یک تستر.
 *
 * ⚠ داده فقط روی دیتابیسِ **develop** است. همین سربرگ روی admin.whosup.ca
 * (پروداکشن) خالی می‌ماند، و این عمدی است: از 2026-10-27 (0190) خودِ تست‌ها هم
 * همه روی develop اجرا می‌شوند، و دستورالعمل و نتیجه ربطی به پروداکشن ندارند.
 *
 * فهرستِ سناریوها از جدولِ qa_scenarios می‌آید که `npm run qa -- sync`
 * از روی qa/scenarios.mjs پرش می‌کند. خالی بودنش یعنی آن اسکریپت هنوز اجرا نشده.
 * فیلتر، «آخرین نتیجه» و شمارشِ دفعات در core/lib/qaReport.ts است، با تست.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { qaRepo } from '@/core/data/qaRepo';
import {
  FIELD_OPS,
  roundEnv,
  buildQaReport,
  defaultTarget,
  inviteKey,
  matchFilter,
  normalizeJiraKey,
  scenarioNotes,
  bugKey,
  bugState,
  type QaBugState,
  scenarioTable,
  ticketLabel,
  roundCandidates,
  STAGE_LABEL,
  ticketTable,
  type QaCond,
  type QaField,
  type QaFilter,
  type QaInvite,
  type QaLink,
  type QaOp,
  type QaRole,
  type QaScenario,
  type QaStateRow,
  type QaStatus,
  type QaTester,
  type QaTicket,
  QA_PLATFORMS, qaNeedText, qaPlatformNeed,
} from '@/core/lib/qaReport';
import { fmtNum as fmt } from '@/ui/Num';
import { Modal } from '@/ui/Modal';
import { ClockCheck } from './ClockCheck';

export const QA_SITE = 'https://qa.palsandplans.com';

const STATUS_LABEL: Record<QaStatus, string> = {
  new: 'شروع‌نشده', progress: 'در جریان', pass: 'قبول', fail: 'رد', blocked: 'مسدود', done: 'انجام شد', review: 'منتظرِ تو',
};
const STATUS_CLASS: Partial<Record<QaStatus, string>> = { pass: 'chip on', fail: 'chip warn', blocked: 'chip warn', review: 'chip warn' };

const CAT_LABEL: Record<string, string> = {
  setup: 'آماده‌سازی', auth: 'ورود و حساب', profile: 'پروفایل و تنظیمات', discover: 'Discover',
  create: 'ساختنِ برنامه', answer: 'جواب و لینکِ دعوت', life: 'بعد از ساختنِ برنامه',
  friends: 'دوستان و گروه‌ها', notif: 'اعلان‌ها', safety: 'حریمِ خصوصی و گزارش', ticket: 'سناریوی تیکت', _ticket: 'سناریوی تیکت',
};
const PLATFORM_LABEL: Record<string, string> = { web: 'وب', android: 'اندروید', ios: 'آیفون', phone: 'گوشی' };
/** «وب و (اندروید یا آیفون)»، «وب یا اندروید یا آیفون» — همان نیازی که تستر در صفحه‌ی QA می‌بیند. */
const platformLabel = (p: string): string => qaNeedText(qaPlatformNeed([p]), PLATFORM_LABEL) || p || '—';
/** محیطِ هر دور develop است (roundEnv، 0190). */
const ENV_LABEL: Record<string, string> = { staging: 'develop' };

const FIELD_LABEL: Record<QaField, string> = {
  text: 'شناسه یا عنوان', ticket: 'تیکت (شماره یا اسم)', source: 'منبع', cat: 'دسته', priority: 'اولویت', platform: 'سکو', smoke: 'Smoke',
  assignee: 'اساین‌شده به', lastStatus: 'آخرین نتیجه', timesTested: 'دفعاتِ تست',
  lastDays: 'روز از آخرین تست',
};
const OP_LABEL: Record<QaOp, string> = {
  is: 'هست', isNot: 'نیست', contains: 'دارد', gte: '≥', lte: '≤', empty: 'خالی است', notEmpty: 'خالی نیست',
};
const NO_VALUE: QaOp[] = ['empty', 'notEmpty'];
/** `.input` is full-width by default; filter controls sit side by side. */
const INLINE = { width: 'auto', marginBottom: 0 } as const;

const hm = (min: number) => (min >= 60 ? `${Math.floor(min / 60)}h ${min % 60}m` : `${min}m`);
const testerName = (t: QaTester) => t.name || t.email;
const when = (ms: number | null) => (ms == null ? null : new Date(ms).toLocaleString('en-CA', { dateStyle: 'short', timeStyle: 'short' }));

/* ===================================================================== تسترها */

/** نامِ قابلِ ویرایش: کلیک → کادر، Enter یا بیرون‌زدن ذخیره، Esc انصراف. */
function NameCell({ name, onSave }: { name: string | null; onSave: (v: string) => Promise<unknown> }) {
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (draft == null) return;
    if (draft.trim() === (name ?? '')) { setDraft(null); return; }
    setBusy(true);
    try { await onSave(draft); setDraft(null); } finally { setBusy(false); }
  };
  if (draft == null) {
    return (
      <button type="button" className="linkbtn" title="ویرایشِ نام" onClick={() => setDraft(name ?? '')}>
        {name ?? <span className="dim">— نام بده</span>} ✎
      </button>
    );
  }
  return (
    <input className="input" autoFocus value={draft} disabled={busy} placeholder="نام"
      onChange={(e) => setDraft(e.target.value)} onBlur={() => void save()}
      onKeyDown={(e) => { if (e.key === 'Enter') void save(); else if (e.key === 'Escape') setDraft(null); }} />
  );
}

/** شناسه‌های دستگاه: با کاما یا فاصله جدا. می‌روند در هر تیکتِ باگِ این تستر (0167). */
function DevicesCell({ ids, onSave }: { ids: string[]; onSave: (v: string[]) => Promise<unknown> }) {
  const [draft, setDraft] = useState<string | null>(null);
  const save = async () => {
    if (draft == null) return;
    const next = [...new Set(draft.split(/[\s,،]+/).map((x) => x.trim()).filter(Boolean))];
    if (next.join(',') !== ids.join(',')) await onSave(next);
    setDraft(null);
  };
  if (draft == null) {
    return (
      <button type="button" className="linkbtn" title="شناسه‌های دستگاه — در تیکتِ باگ می‌آیند" onClick={() => setDraft(ids.join(', '))}>
        {ids.length ? <span className="mono ltr">{ids.join(', ')}</span> : <span className="dim">— افزودن</span>} ✎
      </button>
    );
  }
  return (
    <input className="input ltr mono" autoFocus value={draft} placeholder="id1, id2"
      onChange={(e) => setDraft(e.target.value)} onBlur={() => void save()}
      onKeyDown={(e) => { if (e.key === 'Enter') void save(); else if (e.key === 'Escape') setDraft(null); }} />
  );
}

function Testers({ testers }: { testers: QaTester[] }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const invites = useQuery({ queryKey: ['qa', 'invites'], queryFn: qaRepo.invites });
  const refresh = () => qc.invalidateQueries({ queryKey: ['qa'] });
  const add = useMutation({
    mutationFn: () => qaRepo.addTester(email.trim(), name.trim()),
    onSuccess: (id) => {
      setMsg({ ok: true, text: id ? 'تستر اضافه شد.' : 'این ایمیل هنوز حساب ندارد — دعوت شد. وقتی در سایتِ تست حساب ساخت و ایمیلش را تأیید کرد، با اولین ورود به صفحه‌ی تست تستر می‌شود.' });
      setEmail(''); setName(''); refresh();
    },
    onError: (e: Error) => setMsg({ ok: false, text: e.message }),
  });
  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => qaRepo.setTesterActive(id, active),
    onSuccess: refresh,
  });
  const setDefault = useMutation({ mutationFn: qaRepo.setDefault, onSuccess: refresh });
  const dropInvite = useMutation({ mutationFn: qaRepo.removeInvite, onSuccess: refresh });

  return (
    <div className="card">
      <h2>تسترها</h2>
      <p className="dim" style={{ marginBottom: 10 }}>
        ایمیلی که هنوز حساب ندارد هم اضافه می‌شود و تا ثبت‌نامِ صاحبش «منتظر» می‌ماند. حساب روی سایتِ تست
        ساخته می‌شود (<span className="ltr">dev.goout-1e4.pages.dev</span>). صفحه‌ی تست:{' '}
        <a className="ltr" href={QA_SITE} target="_blank" rel="noreferrer">{QA_SITE}</a>
      </p>
      <div className="filters" style={{ flexWrap: 'wrap', marginBottom: 10 }}>
        <input className="input ltr" placeholder="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <input className="input" placeholder="نام (اختیاری)" value={name} onChange={(e) => setName(e.target.value)} />
        <button type="button" className="btn" disabled={!email.trim() || add.isPending} onClick={() => add.mutate()}>
          افزودن
        </button>
      </div>
      {msg ? <p className={msg.ok ? 'dim' : 'err'} style={{ marginBottom: 8 }}>{msg.text}</p> : null}
      {testers.length || invites.data?.length ? (
        <table className="tbl">
          <thead><tr><th>ایمیل</th><th>نام</th><th>شناسه‌ی دستگاه</th><th>وضعیت</th><th>پیش‌فرض</th><th /></tr></thead>
          <tbody>
            {testers.map((t) => (
              <tr key={t.user_id}>
                <td className="ltr">{t.email}</td>
                <td><NameCell name={t.name} onSave={(v) => qaRepo.setTesterName(t.user_id, v).then(refresh)} /></td>
                <td><DevicesCell ids={t.device_ids ?? []} onSave={(v) => qaRepo.setTesterDevices(t.user_id, v).then(refresh)} /></td>
                <td>
                  <label>
                    <input type="checkbox" checked={t.active}
                      onChange={(e) => toggle.mutate({ id: t.user_id, active: e.target.checked })} /> فعال
                  </label>
                </td>
                <td>
                  <label title="در «اساین به…» از اول انتخاب می‌شود">
                    <input type="radio" name="qa-default" checked={!!t.is_default} disabled={!t.active}
                      onChange={() => setDefault.mutate(t.user_id)} /> پیش‌فرض
                  </label>
                </td>
                <td>
                  {t.is_default ? (
                    <button type="button" className="linkbtn" onClick={() => setDefault.mutate(null)}>برداشتنِ پیش‌فرض</button>
                  ) : null}
                </td>
              </tr>
            ))}
            {(invites.data ?? []).map((i) => (
              <tr key={i.email}>
                <td className="ltr">{i.email}</td>
                <td><NameCell name={i.name} onSave={(v) => qaRepo.setInviteName(i.email, v).then(refresh)} /></td>
                <td><span className="dim">بعد از ثبت‌نام</span></td>
                <td><span className="chip">منتظرِ ثبت‌نام</span></td>
                <td>
                  <label title="در «اساین به…» از اول انتخاب می‌شود">
                    <input type="radio" name="qa-default" checked={!!i.is_default}
                      onChange={() => setDefault.mutate(inviteKey(i.email))} /> پیش‌فرض
                  </label>
                </td>
                <td>
                  {i.is_default ? (
                    <button type="button" className="linkbtn" onClick={() => setDefault.mutate(null)}>برداشتنِ پیش‌فرض</button>
                  ) : null}{' '}
                  <button type="button" className="linkbtn" onClick={() => dropInvite.mutate(i.email)}>لغوِ دعوت</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <p className="dim">هنوز تستری اضافه نشده.</p>}
    </div>
  );
}

/* ===================================================================== فیلترِ و/یا */

function CondRow({ cond, onChange, onRemove, options }: {
  cond: QaCond;
  onChange: (c: QaCond) => void;
  onRemove: () => void;
  options: Partial<Record<QaField, { v: string; label: string }[]>>;
}) {
  const ops = FIELD_OPS[cond.field];
  const choices = options[cond.field];
  return (
    <div className="filters" style={{ gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
      <select className="input" style={INLINE} value={cond.field}
        onChange={(e) => {
          const field = e.target.value as QaField;
          onChange({ field, op: FIELD_OPS[field][0], value: options[field]?.[0]?.v ?? '' });
        }}>
        {(Object.keys(FIELD_LABEL) as QaField[]).map((f) => <option key={f} value={f}>{FIELD_LABEL[f]}</option>)}
      </select>
      <select className="input" style={INLINE} value={cond.op} onChange={(e) => onChange({ ...cond, op: e.target.value as QaOp })}>
        {ops.map((o) => <option key={o} value={o}>{OP_LABEL[o]}</option>)}
      </select>
      {NO_VALUE.includes(cond.op) ? null : choices ? (
        <select className="input" style={INLINE} value={cond.value} onChange={(e) => onChange({ ...cond, value: e.target.value })}>
          {choices.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
        </select>
      ) : (
        <input className="input" style={{ ...INLINE, width: 140 }} value={cond.value}
          inputMode={cond.field === 'text' ? undefined : 'numeric'}
          onChange={(e) => onChange({ ...cond, value: e.target.value })} />
      )}
      <button type="button" className="linkbtn" onClick={onRemove} aria-label="حذفِ شرط">✕</button>
    </div>
  );
}

function FilterBuilder({ filter, setFilter, options }: {
  filter: QaFilter;
  setFilter: (f: QaFilter) => void;
  options: Partial<Record<QaField, { v: string; label: string }[]>>;
}) {
  const blank = (): QaCond => ({ field: 'priority', op: 'is', value: 'P1' });
  const setGroup = (gi: number, g: QaCond[]) => setFilter(filter.map((x, i) => (i === gi ? g : x)).filter((x) => x.length));
  return (
    <div style={{ marginBottom: 10 }}>
      {filter.map((g, gi) => (
        <div key={gi}>
          {gi > 0 ? <div style={{ fontWeight: 700, margin: '6px 0' }}>یا</div> : null}
          <div className="note" style={{ padding: 8 }}>
            {g.map((c, ci) => (
              <div key={ci}>
                {ci > 0 ? <div className="dim" style={{ margin: '2px 0' }}>و</div> : null}
                <CondRow cond={c} options={options}
                  onChange={(nc) => setGroup(gi, g.map((x, i) => (i === ci ? nc : x)))}
                  onRemove={() => setGroup(gi, g.filter((_, i) => i !== ci))} />
              </div>
            ))}
            <button type="button" className="linkbtn" onClick={() => setGroup(gi, [...g, blank()])}>+ و</button>
          </div>
        </div>
      ))}
      <div className="chiprow" style={{ marginTop: 6 }}>
        <button type="button" className="chip" onClick={() => setFilter([...filter, [blank()]])}>
          {filter.length ? '+ یا (گروهِ تازه)' : '+ فیلتر'}
        </button>
        {filter.length ? <button type="button" className="chip" onClick={() => setFilter([])}>پاک کردنِ فیلتر</button> : null}
      </div>
    </div>
  );
}

/* ===================================================================== انتخابِ تیکت */

/** «تیکت» در نوارِ اساین: یکی از موجودها، یا تازه — شماره‌ی Jira یا یک اسمِ آزاد (0166). */
type TicketChoice = { mode: 'existing'; key: string } | { mode: 'jira'; key: string; title: string } | { mode: 'named'; title: string };
const NEW_JIRA = '__new_jira__';
const NEW_NAMED = '__new_named__';

function TicketPicker({ tickets, choice, setChoice }: {
  tickets: QaTicket[]; choice: TicketChoice; setChoice: (c: TicketChoice) => void;
}) {
  const value = choice.mode === 'existing' ? choice.key : choice.mode === 'jira' ? NEW_JIRA : NEW_NAMED;
  return (
    <>
      <select className="input" style={INLINE} value={value}
        onChange={(e) => {
          const v = e.target.value;
          setChoice(v === NEW_JIRA ? { mode: 'jira', key: '', title: '' } : v === NEW_NAMED ? { mode: 'named', title: '' } : { mode: 'existing', key: v });
        }}>
        <option value="">— تیکت —</option>
        {tickets.map((t) => <option key={t.key} value={t.key}>{ticketLabel(t)}</option>)}
        <option value={NEW_JIRA}>+ تیکتِ Jiraی تازه…</option>
        <option value={NEW_NAMED}>+ با یک اسمِ تازه (بی تیکتِ Jira)…</option>
      </select>
      {choice.mode === 'jira' ? (
        <>
          <input className="input ltr" style={{ ...INLINE, width: 130 }} placeholder="WHOSUP-123" value={choice.key}
            onChange={(e) => setChoice({ ...choice, key: e.target.value })} />
          <input className="input" style={{ ...INLINE, width: 220 }} placeholder="عنوان (اختیاری)" value={choice.title}
            onChange={(e) => setChoice({ ...choice, title: e.target.value })} />
        </>
      ) : null}
      {choice.mode === 'named' ? (
        <input className="input" style={{ ...INLINE, width: 260 }} placeholder="اسم — مثلاً «دورِ اولِ وب»" value={choice.title}
          onChange={(e) => setChoice({ ...choice, title: e.target.value })} />
      ) : null}
    </>
  );
}

/** کلیدِ تیکتِ انتخاب‌شده — اگر تازه است همین‌جا ساخته می‌شود. */
async function resolveTicket(choice: TicketChoice): Promise<string> {
  if (choice.mode === 'existing') {
    if (!choice.key) throw new Error('یک تیکت انتخاب کن.');
    return choice.key;
  }
  if (choice.mode === 'jira') {
    const key = normalizeJiraKey(choice.key);
    if (!key) throw new Error('شماره‌ی Jira باید مثلِ WHOSUP-123 باشد.');
    await qaRepo.ensureJiraTicket(key, choice.title);
    return key;
  }
  if (!choice.title.trim()) throw new Error('برای اساینی که تیکتِ Jira نیست یک اسم لازم است.');
  return qaRepo.createNamedTicket(choice.title.trim());
}

/* ===================================================================== جدولِ سناریوها */

const FILTER_KEY = 'qa.admin.filter';

function ScenarioTable({ catalog, tickets, links, testers, invites, review }: {
  catalog: QaScenario[]; tickets: QaTicket[]; links: QaLink[]; testers: QaTester[]; invites: QaInvite[]; review: (id: string) => void;
}) {
  const qc = useQueryClient();
  const assignments = useQuery({ queryKey: ['qa', 'assignments'], queryFn: qaRepo.assignments });
  const events = useQuery({ queryKey: ['qa', 'events'], queryFn: qaRepo.events });
  const [filter, setFilterState] = useState<QaFilter>(() => {
    try {
      const f = JSON.parse(localStorage.getItem(FILTER_KEY) ?? '[]') as QaFilter;
      // a filter saved before 0166 may name a field that no longer exists
      return f.map((g) => g.filter((c) => c.field in FIELD_OPS)).filter((g) => g.length);
    } catch { return []; }
  });
  const setFilter = (f: QaFilter) => {
    setFilterState(f);
    try { localStorage.setItem(FILTER_KEY, JSON.stringify(f)); } catch { /* per-viewer convenience only */ }
  };
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [choice, setChoice] = useState<TicketChoice>({ mode: 'existing', key: '' });
  const [target, setTarget] = useState<string>('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const active = testers.filter((t) => t.active);
  // Invited people who have not signed up yet can be assigned too (0163); their key is invite:<email>.
  const targets = [
    ...active.map((t) => ({ key: t.user_id, label: testerName(t), def: !!t.is_default })),
    ...invites.map((i) => ({ key: inviteKey(i.email), label: `${i.name || i.email} (منتظر)`, def: !!i.is_default })),
  ];
  useEffect(() => { setTarget(defaultTarget(testers, invites) ?? ''); },
    [testers, invites]); // eslint-disable-line react-hooks/exhaustive-deps

  const nameOf = (key: string | null) => targets.find((x) => x.key === key)?.label
    ?? testers.find((x) => x.user_id === key)?.email ?? '—';
  const all = useMemo(() => scenarioTable(catalog, tickets, links, assignments.data ?? [], events.data ?? []),
    [catalog, tickets, links, assignments.data, events.data]);
  const rows = useMemo(() => all.filter((r) => matchFilter(r, filter)), [all, filter]);
  const visibleChecked = rows.filter((r) => checked.has(r.scenario.id)).map((r) => r.scenario.id);

  const refresh = () => qc.invalidateQueries({ queryKey: ['qa'] });
  const assign = useMutation({
    mutationFn: async () => {
      const key = await resolveTicket(choice);
      await qaRepo.addToTicket(visibleChecked, key);
      if (target) await qaRepo.assign(key, target);
      return key;
    },
    onSuccess: (key) => {
      setMsg({ ok: true, text: `${fmt(visibleChecked.length)} سناریو در ${key}${target ? ` — اساین به ${nameOf(target)}` : ''}.` });
      setChoice({ mode: 'existing', key });
      refresh();
    },
    onError: (e: Error) => setMsg({ ok: false, text: e.message }),
  });
  const remove = useMutation({
    mutationFn: async () => {
      if (choice.mode !== 'existing' || !choice.key) throw new Error('تیکتی را انتخاب کن که سناریوها از آن برداشته شوند.');
      await qaRepo.removeFromTicket(visibleChecked, choice.key);
      return choice.key;
    },
    onSuccess: (key) => { setMsg({ ok: true, text: `${fmt(visibleChecked.length)} سناریو از ${key} برداشته شد.` }); refresh(); },
    onError: (e: Error) => setMsg({ ok: false, text: e.message }),
  });

  const options: Partial<Record<QaField, { v: string; label: string }[]>> = {
    cat: [...new Set(catalog.filter((s) => s.kind == null).map((s) => s.cat))].map((c) => ({ v: c, label: CAT_LABEL[c] ?? c })),
    priority: ['P1', 'P2', 'P3'].map((v) => ({ v, label: v })),
    platform: QA_PLATFORMS.map((v) => ({ v, label: PLATFORM_LABEL[v] })),
    smoke: [{ v: 'yes', label: 'بله' }, { v: 'no', label: 'نه' }],
    source: [{ v: 'file', label: 'فایل' }, { v: 'autopilot', label: 'اتوپایلوت' }],
    assignee: targets.map((t) => ({ v: t.key, label: t.label })),
    lastStatus: (['pass', 'fail', 'blocked', 'done'] as QaStatus[]).map((v) => ({ v, label: STATUS_LABEL[v] })),
  };

  const allOn = rows.length > 0 && rows.every((r) => checked.has(r.scenario.id));
  const toggleAll = () => setChecked((prev) => {
    const next = new Set(prev);
    rows.forEach((r) => (allOn ? next.delete(r.scenario.id) : next.add(r.scenario.id)));
    return next;
  });
  const toggle = (id: string) => setChecked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div className="card">
      <h2>سناریوها <span className="dim">({fmt(rows.length)} از {fmt(all.length)})</span></h2>
      <p className="dim" style={{ marginBottom: 8 }}>
        شرط‌های داخلِ یک کادر با «و» به هم وصل‌اند و کادرها با «یا». «هر دو» در سکو هم وب حساب می‌شود و هم اندروید.
        هر اساین در یک «تیکت» می‌نشیند — یک تیکتِ Jira یا یک اسمِ آزاد — و یک سناریو می‌تواند در چند تیکت باشد؛
        نتیجه‌اش در هر تیکت جداست.
      </p>
      <FilterBuilder filter={filter} setFilter={setFilter} options={options} />

      <div className="filters" style={{ flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        <span>{fmt(visibleChecked.length)} ردیفِ انتخاب‌شده →</span>
        <TicketPicker tickets={tickets} choice={choice} setChoice={setChoice} />
        <select className="input" style={INLINE} value={target} onChange={(e) => setTarget(e.target.value)}>
          <option value="">— بی تستر، فقط در تیکت —</option>
          {targets.map((t) => <option key={t.key} value={t.key}>{t.label}{t.def ? ' (پیش‌فرض)' : ''}</option>)}
        </select>
        <button type="button" className="btn" disabled={!visibleChecked.length || assign.isPending}
          onClick={() => { setMsg(null); assign.mutate(); }}>اساین</button>
        <button type="button" className="btn ghost" disabled={!visibleChecked.length || remove.isPending || choice.mode !== 'existing'}
          onClick={() => { setMsg(null); remove.mutate(); }}>برداشتن از این تیکت</button>
      </div>
      {msg ? <p className={msg.ok ? 'dim' : 'err'} style={{ marginBottom: 8 }}>{msg.text}</p> : null}

      <div className="scrollx">
        <table className="tbl">
          <thead>
            <tr>
              <th><input type="checkbox" checked={allOn} onChange={toggleAll} aria-label="انتخابِ همه‌ی ردیف‌های فیلترشده" /></th>
              <th>شناسه</th><th>عنوان</th><th>تیکت‌ها</th><th>دسته</th><th>اولویت</th><th>Smoke</th><th>سکو</th>
              <th>اساین‌شده به</th><th className="num">دفعاتِ تست</th><th>آخرین تست</th><th>آخرین نتیجه</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const s = r.scenario;
              return (
                <tr key={s.id}>
                  <td><input type="checkbox" checked={checked.has(s.id)} onChange={() => toggle(s.id)} /></td>
                  <td><ScenLink id={s.id} review={review} /></td>
                  <td>{s.title.fa}{s.source === 'autopilot' ? <span className="dim"> · اتوپایلوت</span> : null}</td>
                  <td>
                    {r.tickets.length ? r.tickets.map((t) => (
                      <div key={t.key} title={t.title ?? undefined}><span className="mono">{t.key}</span> <span className="dim">{t.title}</span></div>
                    )) : <span className="dim">—</span>}
                  </td>
                  <td>{CAT_LABEL[s.cat] ?? s.cat}</td>
                  <td>{s.priority ?? <span className="dim">—</span>}</td>
                  <td>{s.smoke ? '✓' : ''}</td>
                  <td>{platformLabel(s.platform)}</td>
                  <td>{r.assignees.length ? r.assignees.map(nameOf).join('، ') : <span className="dim">—</span>}</td>
                  <td className="num">{fmt(r.timesTested)}</td>
                  <td className="ltr dim">{when(r.lastAt) ?? '—'}</td>
                  <td>
                    {r.lastStatus ? (
                      <>
                        <span className={STATUS_CLASS[r.lastStatus] ?? ''}>{STATUS_LABEL[r.lastStatus]}</span>
                        <span className="dim"> · {nameOf(r.lastBy)}{r.lastTicket ? ` · ${r.lastTicket}` : ''}</span>
                      </>
                    ) : <span className="dim">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ===================================================================== تیکت‌ها */

const ROLE_LABEL: Record<QaRole, string> = { sanity: 'Sanity', acceptance: 'پذیرش', regression: 'رگرسیون', assigned: 'اساین‌شده' };
const SOURCE_LABEL: Record<string, string> = { file: 'فایل', autopilot: 'اتوپایلوت', admin: 'ادمین' };

function Tickets({ catalog, tickets, links, testers, invites, review }: {
  catalog: QaScenario[]; tickets: QaTicket[]; links: QaLink[]; testers: QaTester[]; invites: QaInvite[]; review: (id: string) => void;
}) {
  const qc = useQueryClient();
  const assignments = useQuery({ queryKey: ['qa', 'assignments'], queryFn: qaRepo.assignments });
  const events = useQuery({ queryKey: ['qa', 'events'], queryFn: qaRepo.events });
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState<Record<string, string>>({});
  const [showAll, setShowAll] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['qa'] });
  const assign = useMutation({ mutationFn: ({ key, who }: { key: string; who: string }) => qaRepo.assign(key, who), onSuccess: refresh });
  const unassign = useMutation({ mutationFn: ({ key, who }: { key: string; who: string }) => qaRepo.unassign(key, who), onSuccess: refresh });
  const drop = useMutation({ mutationFn: qaRepo.deleteTicket, onSuccess: refresh });
  const setOpenRound = useMutation({ mutationFn: ({ key, on }: { key: string; on: boolean }) => qaRepo.setOpen(key, on), onSuccess: refresh });
  /* فقط تیکتی که ادمین ساخته؛ اسمِ تیکتِ فایل یا اتوپایلوت در اجرای بعدیِ sync / put-ticket دوباره نوشته می‌شود. */
  const rename = async (t: QaTicket, title: string) => {
    if (t.kind === 'named' && !title.trim()) { alert('تیکتِ نام‌دار بی‌اسم نمی‌شود.'); return; }
    await qaRepo.renameTicket(t.key, title);
    await refresh();
  };
  const newRound = useMutation({
    mutationFn: ({ key, ids }: { key: string; ids: string[] }) => {
      const n = tickets.filter((x) => x.jira_key === key).length + 2;
      return qaRepo.newRound(key, `${key} · دورِ ${n}`, ids);
    },
    onSuccess: (k) => { refresh(); alert(`دورِ ${k} ساخته شد — به همان تسترها، یا آزاد برای همه اگر دورِ اصلی هنوز آزاد است.`); },
    onError: (e) => alert((e as Error).message),
  });
  const startRound = (key: string) => {
    const ids = roundCandidates(key, rows).map((s) => s.id);
    if (!ids.length) { alert('همه‌ی سناریوهای این تیکت آخرین بار قبول شده‌اند؛ دورِ تازه لازم نیست.'); return; }
    if (confirm(`دورِ تازه برای ${key} با ${ids.length} سناریو (هر سناریویی که آخرین نتیجه‌اش «قبول» نیست):\n${ids.join('\n')}\n\nبه همان تسترهای ${key} اساین شود؟`)) {
      newRound.mutate({ key, ids });
    }
  };
  const removeOne = useMutation({ mutationFn: ({ key, id }: { key: string; id: string }) => qaRepo.removeFromTicket([id], key), onSuccess: refresh });

  const targets = [
    ...testers.filter((t) => t.active).map((t) => ({ key: t.user_id, label: testerName(t) })),
    ...invites.map((i) => ({ key: inviteKey(i.email), label: `${i.name || i.email} (منتظر)` })),
  ];
  const nameOf = (key: string | null) => targets.find((x) => x.key === key)?.label ?? testers.find((t) => t.user_id === key)?.email ?? '—';
  const triage = useQuery({ queryKey: ['qa', 'triage'], queryFn: qaRepo.triageEffects });
  const rows = useMemo(() => ticketTable(tickets, links, catalog, assignments.data ?? [], events.data ?? [], triage.data ?? []),
    [tickets, links, catalog, assignments.data, events.data, triage.data]);
  const shown = showAll ? rows : rows.filter((r) => !r.finished);

  return (
    <div className="card">
      <h2>دورهای تست <span className="dim">({fmt(shown.length)}{showAll ? '' : ` فعال از ${fmt(rows.length)}`})</span></h2>
      <p className="dim" style={{ marginBottom: 8 }}>
        تستر هر دوری را که به او اساین شده با همه‌ی سناریوهایش می‌بیند. دورِ «آزاد برای همه» به هیچ‌کس اساین نیست:
        همه‌ی تسترها آن را می‌بینند و اولین کسی که برش دارد صاحبش می‌شود (اتوپایلوت دورها را همین‌طور می‌سازد). دورِ یک تیکتِ Jira — خودِ تیکت و هر «دورِ تازه»
        که برایش ساخته شده — وقتی برای هر سناریو نتیجه‌ای هست، تیکت را در Jira جابه‌جا می‌کند (qa-jira)؛ آخرین نتیجه‌ی
        هر سناریو در هر کدام از این دورها حساب است. دورِ نام‌دارِ بی‌تیکت فقط همین‌جا دیده می‌شود. دوری که همه‌ی
        سناریوهایش نتیجه گرفته‌اند «تمام‌شده» است و از این فهرست و از صفحه‌ی تستر کنار می‌رود.
        از دورِ دوم به بعدِ یک تیکت، سناریوها سه مرحله دارند: اول آن‌چه آخرین بار رد شده بود، بعد آن‌چه دست‌کم یک بار
        رد شده بود، بعد بقیه. مرحله‌ای که کامل قبول نشود دور را همان‌جا رد می‌کند و بقیه «اجرا نشد» می‌مانند.
        «مسدود» نه قبول است نه رد: دور و تیکتِ Jira منتظر می‌مانند تا همان سناریو دوباره اجرا شود (مگر «رد»ِ دیگری
        کنارش باشد)، و مدیر همان لحظه در تلگرام با علتش خبر می‌گیرد.
      </p>
      <label className="dim" style={{ display: 'block', marginBottom: 8 }}>
        <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> نمایشِ دورهای تمام‌شده هم
      </label>
      {shown.length ? (
        <table className="tbl">
          <thead>
            <tr><th>دور</th><th>نوع</th><th className="num">سناریو</th><th>نتیجه</th><th>تسترها</th><th /></tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const t = r.ticket;
              const free = targets.filter((x) => !r.assignees.includes(x.key));
              return (
                <React.Fragment key={t.key}>
                  <tr>
                    <td>
                      <button type="button" className="linkbtn" onClick={() => setOpen(open === t.key ? null : t.key)}>
                        {open === t.key ? '▾' : '▸'} <span className="mono">{t.key}</span>
                      </button>{' '}
                      {t.source === 'admin' ? <NameCell name={t.title} onSave={(v) => rename(t, v)} />
                        : <span title="اسمش از فایل یا اتوپایلوت می‌آید و همان‌جا عوض می‌شود">{t.title}</span>}
                    </td>
                    <td className="dim">
                      {t.kind === 'jira' ? 'Jira' : t.jira_key ? <>دورِ تازه‌ی <span className="mono">{t.jira_key}</span></> : 'نام‌دار'}
                      {' · '}{ENV_LABEL[roundEnv(t)]}
                      {' · '}{SOURCE_LABEL[t.source] ?? t.source}{r.finished ? ' · تمام‌شده' : ''}
                    </td>
                    <td className="num">{fmt(r.scenarios.length)}</td>
                    <td>
                      {fmt(r.decided)} / {fmt(r.scenarios.length)}
                      {r.pass ? <span className="chip on"> {fmt(r.pass)} قبول</span> : null}
                      {r.fail ? <span className="chip warn"> {fmt(r.fail)} رد</span> : null}
                      {r.blocked ? <span className="chip warn"> {fmt(r.blocked)} مسدود</span> : null}
                      {r.stoppedAt != null ? (
                        <span className="chip warn" title="یک مرحله کامل قبول نشد؛ مرحله‌های بعد لازم نبود و دور خودکار رد شد">
                          {' '}⛔ رد در مرحله‌ی {fmt(r.stoppedAt)}
                        </span>
                      ) : null}
                    </td>
                    <td>
                      {t.open ? (
                        <span className="chip on" style={{ marginInlineEnd: 4 }} title="هنوز کسی برنداشته؛ اولین تستری که بردارد صاحبش می‌شود">
                          آزاد برای همه{' '}
                          <button type="button" className="linkbtn" aria-label="بستنِ دورِ آزاد"
                            onClick={() => setOpenRound.mutate({ key: t.key, on: false })}>✕</button>
                        </span>
                      ) : !r.assignees.length && !r.finished ? (
                        <button type="button" className="linkbtn" style={{ marginInlineEnd: 4 }}
                          title="همه‌ی تسترها می‌بینند؛ اولین کسی که بردارد صاحبش می‌شود"
                          onClick={() => setOpenRound.mutate({ key: t.key, on: true })}>آزاد برای همه</button>
                      ) : null}
                      {r.assignees.map((a) => (
                        <span key={a} className="chip" style={{ marginInlineEnd: 4 }}>
                          {nameOf(a)}{' '}
                          <button type="button" className="linkbtn" aria-label="برداشتنِ اساین"
                            onClick={() => unassign.mutate({ key: t.key, who: a })}>✕</button>
                        </span>
                      ))}
                      {free.length ? (
                        <span style={{ whiteSpace: 'nowrap' }}>
                          <select className="input" style={INLINE} value={adding[t.key] ?? ''}
                            onChange={(e) => setAdding({ ...adding, [t.key]: e.target.value })}>
                            <option value="">+ تستر…</option>
                            {free.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                          </select>
                          <button type="button" className="linkbtn" disabled={!adding[t.key]}
                            onClick={() => { assign.mutate({ key: t.key, who: adding[t.key] }); setAdding({ ...adding, [t.key]: '' }); }}>
                            اساین
                          </button>
                        </span>
                      ) : null}
                    </td>
                    <td>
                      {t.kind === 'jira' ? (
                        <button type="button" className="linkbtn" disabled={newRound.isPending} onClick={() => startRound(t.key)}
                          title="یک دورِ تازه برای همین تیکت، با سناریوهایی که آخرین نتیجه‌شان قبول نیست و همان تسترها">
                          دورِ تازه
                        </button>
                      ) : null}{' '}
                      {t.source === 'admin' ? (
                        <button type="button" className="linkbtn"
                          onClick={() => { if (confirm(`دورِ ${t.key} و اساین‌هایش پاک شود؟ سناریوها و نتیجه‌های ثبت‌شده‌ی تسترها می‌مانند.`)) drop.mutate(t.key); }}>
                          حذف
                        </button>
                      ) : <span className="dim" title="این تیکت را فایل یا اتوپایلوت ساخته و از همان‌جا عوض می‌شود">—</span>}
                    </td>
                  </tr>
                  {open === t.key ? (
                    <tr>
                      <td colSpan={6}>
                        {r.scenarios.length ? r.scenarios.map((x) => (
                          <div key={x.scenario.id} style={{ paddingInlineStart: 16 }}>
                            {x.stage != null ? <span className="dim" title={STAGE_LABEL[x.stage]}>مرحله‌ی {fmt(x.stage)} · </span> : null}
                            <span className="dim">{ROLE_LABEL[x.role]}</span> · <ScenLink id={x.scenario.id} review={review} /> {x.scenario.title.fa}{' '}
                            {x.skipped ? <span className="dim">اجرا نشد — دور زودتر رد شد</span> : x.attempts.length ? x.attempts.map((a, i) => (
                              <span key={i} title={`${nameOf(a.by)} · ${when(a.at)}`} style={{ marginInlineEnd: 4 }}>
                                <span className="dim">بارِ {a.n ?? i + 1}</span>{' '}
                                <span className={STATUS_CLASS[a.status] ?? ''}>{STATUS_LABEL[a.status]}</span>
                                <span className="dim"> · {nameOf(a.by)}</span>
                              </span>
                            )) : <span className="dim">بی نتیجه</span>}
                            {x.role === 'assigned' ? (
                              <>{' '}<button type="button" className="linkbtn" onClick={() => removeOne.mutate({ key: t.key, id: x.scenario.id })}>برداشتن</button></>
                            ) : null}
                          </div>
                        )) : <span className="dim">سناریویی ندارد.</span>}
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      ) : <p className="dim">{rows.length ? 'دورِ فعالی نیست.' : 'هنوز دوری نیست — از جدولِ سناریوها چند ردیف را انتخاب کن و «اساین» بزن.'}</p>}
    </div>
  );
}

/* ===================================================================== عکس */

function Photo({ userId, id }: { userId: string; id: string }) {
  const q = useQuery({ queryKey: ['qa', 'photo', userId, id], queryFn: () => qaRepo.photo(userId, id), staleTime: Infinity });
  if (!q.data) return <span className="dim">…</span>;
  return <a href={q.data} target="_blank" rel="noreferrer"><img src={q.data} alt="" style={{ height: 64, borderRadius: 4 }} /></a>;
}

/* ===================================================================== باگ‌ها */

const JIRA_BROWSE = 'https://parvij.atlassian.net/browse/';
const BUG_STATE_LABEL: Record<QaBugState, string> = { open: 'باز', handled: 'رسیدگی شد', done: 'تیکتش انجام شد' };
const BUG_SHOW_KEY = 'qa.admin.bugShow';

/**
 * هر باگ با تیکتِ Jiraاش (0167)، علامتِ «رسیدگی شد» (0168) و وضعیتِ تیکت که qa-jira
 * از Jira می‌خواند. پیش‌فرض فقط باگ‌های باز؛ «رسیدگی شد» و «تیکتش انجام شد» با
 * «همه» دیده می‌شوند. کدام باز است: bugState در core/lib/qaReport.ts.
 */
function Bugs({ bugs, nameOf, review }: {
  bugs: { user_id: string; id: string; sid: string; k: string | null; p: number; t: number; text: string; sev: string | null; photos: string[] }[];
  nameOf: (uid: string) => string; review: (id: string) => void;
}) {
  const qc = useQueryClient();
  const jira = useQuery({ queryKey: ['qa', 'bugJira'], queryFn: qaRepo.bugJira, refetchInterval: 60_000 });
  const handled = useQuery({ queryKey: ['qa', 'bugHandled'], queryFn: qaRepo.bugHandled });
  const [all, setAllState] = useState(() => { try { return localStorage.getItem(BUG_SHOW_KEY) === 'all'; } catch { return false; } });
  const setAll = (v: boolean) => { setAllState(v); try { localStorage.setItem(BUG_SHOW_KEY, v ? 'all' : 'open'); } catch { /* per-viewer convenience only */ } };
  const mark = useMutation({
    mutationFn: ({ uid, id, on }: { uid: string; id: string; on: boolean }) => qaRepo.setBugHandled(uid, id, on),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['qa', 'bugHandled'] }),
  });
  const refresh = useMutation({
    mutationFn: qaRepo.refreshBugStatus,
    // The function answers into qa_bug_jira a few seconds later.
    onSuccess: () => setTimeout(() => qc.invalidateQueries({ queryKey: ['qa', 'bugJira'] }), 8000),
  });
  const jiraBy = useMemo(() => new Map((jira.data ?? []).map((x) => [bugKey(x.user_id, x.note_id), x])), [jira.data]);
  const handledBy = useMemo(() => new Map((handled.data ?? []).map((x) => [bugKey(x.user_id, x.note_id), x])), [handled.data]);
  const rows = bugs.map((n) => {
    const key = bugKey(n.user_id, n.id);
    return { n, key, j: jiraBy.get(key), state: bugState(jiraBy.get(key), handledBy.get(key)) };
  });
  const open = rows.filter((x) => x.state === 'open');
  const shown = all ? rows : open;
  const checked = (jira.data ?? []).map((x) => x.status_checked_at).filter(Boolean).sort().pop();

  return (
    <div className="card">
      <div className="filters" style={{ marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
        <h2 style={{ margin: 0 }}>باگ‌ها <span className="dim">({fmt(open.length)} باز از {fmt(rows.length)})</span></h2>
        <span style={{ flex: 1 }} />
        <div className="chiprow">
          <button type="button" className={all ? 'chip' : 'chip on'} onClick={() => setAll(false)}>فقط باز</button>
          <button type="button" className={all ? 'chip on' : 'chip'} onClick={() => setAll(true)}>همه</button>
        </div>
        <button type="button" className="btn ghost" disabled={refresh.isPending} onClick={() => refresh.mutate()}
          title="وضعیتِ تیکت‌ها را از Jira دوباره می‌خواند (خودکار هم هر ۱۵ دقیقه)">
          به‌روزرسانی از Jira
        </button>
      </div>
      <p className="dim" style={{ marginBottom: 8 }}>
        باگی که «رسیدگی شد» بخورد یا تیکتش در Jira «Done» شود از فهرستِ پیش‌فرض بیرون می‌رود.
        {checked ? <> آخرین خواندن از Jira: <span className="ltr">{when(Date.parse(checked))}</span></> : null}
        {refresh.error ? <span className="err"> — {(refresh.error as Error).message}</span> : null}
      </p>
      {shown.length ? shown.map(({ n, key, j, state }) => (
        <div key={key} className="note" style={{ marginBottom: 10, opacity: state === 'open' ? 1 : 0.65 }}>
          <div className="dim" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            {j?.issue_key ? (
              <a className="mono ltr" href={JIRA_BROWSE + j.issue_key} target="_blank" rel="noreferrer"
                title={j.jira_status ? `وضعیت در Jira: ${j.jira_status}` : undefined}>
                {j.issue_key}{j.jira_status ? ` · ${j.jira_status}` : ''}
              </a>
            ) : j?.error && j.error !== 'pending' ? <span className="err" title={j.error}>تیکت ساخته نشد</span>
              : <span title="تیکتِ Jira هنوز ساخته نشده">بی تیکت</span>}
            <span>·</span>
            <span className="mono">{n.k ? `${n.k} · ` : ''}</span><ScenLink id={n.sid} review={review} /> · قدمِ {n.p} · {nameOf(n.user_id)} ·
            <span className="chip warn">{n.sev ?? 'bug'}</span>
            <span className="ltr">{new Date(n.t).toLocaleString('en-CA')}</span>
            <span style={{ flex: 1 }} />
            {state !== 'open' ? <span className="chip on">{BUG_STATE_LABEL[state]}</span> : null}
            <label title="از فهرستِ پیش‌فرض بیرون می‌رود">
              <input type="checkbox" checked={state === 'handled'} disabled={mark.isPending}
                onChange={(e) => mark.mutate({ uid: n.user_id, id: n.id, on: e.target.checked })} /> رسیدگی شد
            </label>
          </div>
          {n.text ? <div style={{ whiteSpace: 'pre-wrap' }}>{n.text}</div> : null}
          {n.photos.length ? (
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              {n.photos.map((p) => <Photo key={p} userId={n.user_id} id={p} />)}
            </div>
          ) : null}
        </div>
      )) : <p className="dim">{rows.length ? 'باگِ بازی نمانده.' : 'باگی ثبت نشده.'}</p>}
    </div>
  );
}

/* ===================================================================== مرورِ سناریو */

/** جایگذاری‌های متنِ سناریو ([[B]]، [[@B]]، [[web]]، [[admin]] — QA_TOKENS) برای ادمین: هر تستر ایمیل و نامِ کاربریِ خودش را می‌بیند. */
const tok = (s: string | undefined) => (s ?? '')
  .replace(/\[\[([A-D])\]\]/g, '‹ایمیلِ $1›').replace(/\[\[@([A-D])\]\]/g, '‹نامِ کاربریِ $1›').replace(/\[\[web\]\]/g, '‹سایت›').replace(/\[\[admin\]\]/g, '‹ادمین›');

/** شناسه‌ی سناریو که با کلیک پنجره‌ی مرورش را باز می‌کند. */
function ScenLink({ id, review }: { id: string; review: (id: string) => void }) {
  return <button type="button" className="linkbtn mono" title="مرورِ قدم‌ها و یادداشت‌ها" onClick={() => review(id)}>{id}</button>;
}

/**
 * همه‌ی سناریو یک‌جا: پیش‌نیاز، هر قدم با «چه می‌بینی»، نتیجه‌ی مورد انتظار — و
 * زیرِ هر کدام یادداشت‌ها و باگ‌هایی که تسترها روی همان صفحه نوشته‌اند (scenarioNotes).
 */
function ScenarioReview({ scenario, id, states, nameOf, onClose }: {
  scenario: QaScenario | undefined; id: string; states: QaStateRow[]; nameOf: (uid: string) => string; onClose: () => void;
}) {
  const notes = useMemo(() => scenarioNotes(states, id), [states, id]);
  if (!scenario) return <Modal title={id} onClose={onClose}><p className="dim">این سناریو در فهرست نیست.</p></Modal>;
  const b = scenario.body ?? {};
  const steps = b.steps ?? [];
  const bugCount = [...notes.values()].flat().filter((n) => n.bug).length;
  const Notes = ({ p }: { p: number }) => {
    const ns = notes.get(p);
    if (!ns?.length) return null;
    return (
      <div style={{ marginTop: 6 }}>
        {ns.map((n) => (
          <div key={`${n.user_id}/${n.id}`} className="note" style={{ marginBottom: 6, padding: 8 }}>
            <div className="dim">
              {n.bug ? <><span className="chip warn">🐞 {n.sev ?? 'bug'}</span> · </> : null}
              {nameOf(n.user_id)}{n.k ? <> · <span className="mono">{n.k}</span></> : null} · بارِ {n.a} ·{' '}
              <span className="ltr">{when(n.t)}</span>
            </div>
            {n.text ? <div style={{ whiteSpace: 'pre-wrap' }}>{n.text}</div> : null}
            {n.photos.length ? (
              <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                {n.photos.map((ph) => <Photo key={ph} userId={n.user_id} id={ph} />)}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    );
  };
  return (
    <Modal wide title={`${scenario.id} · ${scenario.title.fa}`} onClose={onClose}
      hint={[CAT_LABEL[scenario.cat] ?? scenario.cat, platformLabel(scenario.platform),
        scenario.priority, scenario.smoke ? 'Smoke' : null,
        `نسخه‌ی ${scenario.v}`, bugCount ? `${fmt(bugCount)} باگ` : null].filter(Boolean).join(' · ')}>
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <h4>نمای کلی</h4>
        {b.accounts ? <p><b>حساب‌ها:</b> <span className="ltr">{b.accounts}</span></p> : null}
        {b.pre ? <p style={{ whiteSpace: 'pre-wrap' }}><b>پیش‌نیاز:</b> {tok(b.pre.fa)}</p> : null}
        <Notes p={0} />
        {steps.map((st, i) => (
          <div key={i} style={{ borderTop: '1px solid var(--line)', paddingTop: 8, marginTop: 8 }}>
            <h4>قدمِ {fmt(i + 1)} از {fmt(steps.length)}</h4>
            <p style={{ whiteSpace: 'pre-wrap' }}>{tok(st.fa)}</p>
            {st.check ? <p className="dim" style={{ whiteSpace: 'pre-wrap' }}><b>چه می‌بینی:</b> {tok(st.check.fa)}</p> : null}
            <Notes p={i + 1} />
          </div>
        ))}
        <div style={{ borderTop: '1px solid var(--line)', paddingTop: 8, marginTop: 8 }}>
          <h4>نتیجه‌ی مورد انتظار</h4>
          {b.expected ? <p style={{ whiteSpace: 'pre-wrap' }}>{tok(b.expected.fa)}</p> : <p className="dim">—</p>}
          <Notes p={steps.length + 1} />
        </div>
      </div>
      <div style={{ marginTop: 10 }}><button type="button" className="btn" onClick={onClose}>بستن</button></div>
    </Modal>
  );
}

/* ===================================================================== سربرگ */

export function QaTab() {
  const catalog = useQuery({ queryKey: ['qa', 'catalog'], queryFn: qaRepo.catalog });
  const tickets = useQuery({ queryKey: ['qa', 'tickets'], queryFn: qaRepo.tickets });
  const links = useQuery({ queryKey: ['qa', 'links'], queryFn: qaRepo.links });
  const assignments = useQuery({ queryKey: ['qa', 'assignments'], queryFn: qaRepo.assignments });
  const testers = useQuery({ queryKey: ['qa', 'testers'], queryFn: qaRepo.testers });
  const states = useQuery({ queryKey: ['qa', 'states'], queryFn: qaRepo.states });
  const events = useQuery({ queryKey: ['qa', 'events'], queryFn: qaRepo.events });
  const invites = useQuery({ queryKey: ['qa', 'invites'], queryFn: qaRepo.invites });
  const past = useQuery({ queryKey: ['qa', 'pastOwners'], queryFn: qaRepo.pastOwners });
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [review, setReview] = useState<string | null>(null);

  const report = useMemo(() => (catalog.data && tickets.data && links.data && assignments.data && testers.data && states.data
    ? buildQaReport(catalog.data, tickets.data, links.data, assignments.data, testers.data, states.data, events.data ?? [], past.data ?? [])
    : null), [catalog.data, tickets.data, links.data, assignments.data, testers.data, states.data, events.data, past.data]);

  const error = [catalog, tickets, links, assignments, testers, states].find((q) => q.error)?.error as Error | undefined;
  const nameOf = (uid: string) => report?.testers.find((t) => t.tester.user_id === uid)?.displayName ?? uid;
  const shared = catalog.data && tickets.data && links.data
    ? { catalog: catalog.data, tickets: tickets.data, links: links.data, testers: testers.data ?? [], invites: invites.data ?? [], review: setReview }
    : null;

  return (
    <div>
      {error ? <p className="err">{error.message} — مایگریشن‌های 0161 تا 0166 روی این دیتابیس اجرا شده‌اند؟</p> : null}
      <Testers testers={testers.data ?? []} />

      {report && report.testers.length ? (
        <>
          <div className="card">
            <h2>پیشرفت</h2>
            <table className="tbl">
              <thead>
                <tr>
                  <th>تستر</th><th className="num">انجام‌شده</th><th className="num">قبول</th>
                  <th className="num">رد</th><th className="num">مسدود</th><th className="num">باگ</th>
                  <th className="num">ساعتِ اعلام‌شده</th><th className="num">زمانِ روی سناریو</th><th>آخرین تغییر</th>
                </tr>
              </thead>
              <tbody>
                {report.testers.map((t) => (
                  <tr key={t.tester.user_id}>
                    <td>{t.displayName}</td>
                    <td className="num">{fmt(t.done)} / {fmt(t.assigned)}</td>
                    <td className="num">{fmt(t.pass)}</td>
                    <td className="num">{fmt(t.fail)}</td>
                    <td className="num">{fmt(t.blocked)}</td>
                    <td className="num">{fmt(t.bugs)}</td>
                    <td className="num ltr">{hm(t.hoursMin)}</td>
                    <td className="num ltr" title="از روی ساعتی که صفحه هنگامِ باز بودنِ هر سناریو روشن نگه می‌دارد">{hm(t.clockMin)}</td>
                    <td className="dim ltr">{t.updatedAt ? new Date(t.updatedAt).toLocaleString('en-CA') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* «نظرِ تستر» (0186) از WHOSUP-382 (0193) پرونده‌ی بررسی است — تصمیم فقط در صفحه‌ی تست، سربرگِ «پرونده‌ها». */}
          <p className="dim">💭 «نظرِ خودِ تستر» و بقیه‌ی پرونده‌ها حالا در <a href="https://qa.palsandplans.com/" target="_blank" rel="noreferrer">صفحه‌ی تست</a>، سربرگِ «پرونده‌ها» تصمیم گرفته می‌شوند.</p>
          <Bugs bugs={report.bugs} nameOf={nameOf} review={setReview} />
          {catalog.data ? <ClockCheck testers={testers.data ?? []} catalog={catalog.data} /> : null}
        </>
      ) : null}

      {shared ? <Tickets {...shared} /> : null}

      {report && report.rows.length ? (
        <div className="card">
          <div className="filters" style={{ marginBottom: 8 }}>
            <h2 style={{ margin: 0 }}>نتیجه‌ی هر بارِ هر سناریو در هر تیکت</h2>
            <span style={{ flex: 1 }} />
            <label><input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} /> فقط انجام‌نشده‌ها و ردشده‌ها</label>
          </div>
          <div className="scrollx">
            <table className="tbl">
              <thead>
                <tr><th>تیکت</th><th>سناریو</th><th>بار</th>{report.testers.map((t) => <th key={t.tester.user_id}>{t.displayName}</th>)}<th>یادداشت‌ها</th></tr>
              </thead>
              <tbody>
                {report.rows
                  .filter((r) => !onlyOpen || Object.values(r.status).some((s) => s != null && s !== 'pass' && s !== 'done'))
                  .map((r) => (
                    <tr key={`${r.ticket.key}/${r.scenario.id}/${r.attempt}`}>
                      <td className="mono" title={r.ticket.title ?? undefined}>{r.ticket.key}</td>
                      <td><ScenLink id={r.scenario.id} review={setReview} /> <span className="dim">{r.scenario.title.fa}</span></td>
                      <td className="num">{fmt(r.attempt)}</td>
                      {report.testers.map((t) => {
                        const s = r.status[t.tester.user_id];
                        return (
                          <td key={t.tester.user_id}>
                            {s === null ? <span className="dim" title="این تیکت به این تستر اساین نشده">·</span>
                              : s === undefined ? <span className="dim" title="این تستر این بار را اجرا نکرده">—</span>
                                : <span className={STATUS_CLASS[s] ?? 'dim'}>{STATUS_LABEL[s]}</span>}
                          </td>
                        );
                      })}
                      <td>
                        {r.notes.map((n) => (
                          <div key={`${n.user_id}/${n.id}`} className={n.bug ? 'err' : 'dim'}>
                            {n.bug ? '🐞 ' : ''}قدمِ {n.p}: {n.text || '(عکس)'}
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {catalog.data && !catalog.data.length ? (
        <div className="card"><p className="dim">فهرستِ سناریوها خالی است — <span className="mono ltr">npm run qa -- sync</span> را اجرا کن.</p></div>
      ) : null}
      {shared && shared.catalog.length ? <ScenarioTable {...shared} /> : null}
      {review && catalog.data ? (
        <ScenarioReview scenario={catalog.data.find((x) => x.id === review)} id={review} states={states.data ?? []}
          nameOf={nameOf} onClose={() => setReview(null)} />
      ) : null}
    </div>
  );
}
